// src/components/CampaignROIExport.jsx
import { useState } from "react";
import { Button, useToast } from "@chakra-ui/react";
import { supabase } from "../utils/supabaseClient";
import { exportToCSV } from "../utils/exportToCSV";

/**
 * CampaignROIExport
 * Exports campaign ROI intelligence to CSV.
 * - Calls RPC update_campaign_intelligence with integer businessId.
 * - Builds rows with postcode, saturation, ROI, and velocity.
 */
export default function CampaignROIExport({ businessId, campaignId }) {
  const [loading, setLoading] = useState(false);
  const toast = useToast();

  const handleExport = async () => {
    if (!businessId || !campaignId) {
      toast({
        title: "Missing context",
        description: "Business or campaign ID is missing.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setLoading(true);
    try {
      const { data, error } = await supabase.rpc("update_campaign_intelligence", {
        business_id: businessId, // ✅ integer ID
      });

      if (error) throw error;
      if (!data || !Array.isArray(data)) {
        throw new Error("No data returned from RPC.");
      }

      const rows = data.map((row) => ({
        Postcode: row.postcode_sector || "—",
        Saturation:
          row.saturation !== null && row.saturation !== undefined
            ? (row.saturation * 100).toFixed(1) + "%"
            : "—",
        ROI:
          row.roi_estimate !== null && row.roi_estimate !== undefined
            ? (row.roi_estimate * 100).toFixed(1) + "%"
            : "—",
        Velocity: row.redemption_velocity_7d ?? "—",
      }));

      exportToCSV(`campaign_roi_${campaignId}`, rows);

      toast({
        title: "ROI Exported",
        description: `${rows.length} rows exported to CSV.`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (err) {
      console.error("ROI export error:", err);
      toast({
        title: "Export failed",
        description: err.message || "Unexpected error during export.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button
      size="sm"
      colorScheme="blue"
      onClick={handleExport}
      isLoading={loading}
      loadingText="Exporting..."
    >
      Export ROI CSV
    </Button>
  );
}
